import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import { down, up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import z, * as zod from 'zod';
import { dayjs } from '@lgg/isomorphic/dayjs';
import { roundToMinutesInterval } from '@lgg/isomorphic/round-to-minutes-interval';
import {
  Query,
  QueryScheduleArgs,
  Schedule,
  SchedulePriority,
  ScheduleStatus,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AttendeeCandidate } from 'src/components/domain/attendees/attendee-candidates-select';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import {
  InlineTextArea,
  BigTextInput,
  InlineInputWrapper,
  StyledInlineTextInput,
} from 'src/components/general/inputs/inline-text-inputs';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import { InlineFormSelect } from 'src/components/general/modals/inline-form/inline-form-select';
import {
  InlineFormUserSelect,
  AttendeesSelect,
  InlineFormContactSelect,
  UserSelectPlaceholder,
  UserSelectValue,
  useShowCloseConfirmationModal,
  InlineFormDeleteOption,
} from 'src/components/general/modals/inline-form/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  useAppointmentsPriorityOptions,
  useAppointmentsStatusOptions,
} from 'src/components/pages/appointments/hooks';
import { useDeleteAppointment } from 'src/hooks/gql/use-delete-appointment';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useModalUrl } from 'src/hooks/use-modal-url';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';
import { copyUrlToClipboard } from 'src/utils/copy-to-clipboard-helpers';
import { Tooltip } from '../../display/tooltip';
import { Icon } from '../../icon';
import { SelectOption } from '../../inputs/select/select';
import { AppointmentDateTimePicker } from './appointment-date-time-picker';
import { DesktopSelect } from './task-inline-form';

const APPOINTMENT_FRAGMENT = gql`
  fragment AppointmentFragment on Schedule {
    id
    title
    status
    description
    startAt
    endAt
    priority
    isAllDay
    createdAt
    attendees {
      ... on ScheduleAttendee {
        id
      }
      ... on ScheduleContactAttendee {
        contact {
          id
          label
        }
      }
      ... on ScheduleUserAttendee {
        user {
          fullName
          id
        }
      }
      ... on ScheduleEmailAttendee {
        email
      }
    }
    user {
      id
      fullName
    }
    contact {
      id
    }
  }
`;

const CREATE_APPOINTMENT_MUTATION = gql`
  ${APPOINTMENT_FRAGMENT}
  mutation CreateAppointment($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      schedule {
        ...AppointmentFragment
      }
    }
  }
`;

const UPDATE_APPOINTMENT_MUTATION = gql`
  ${APPOINTMENT_FRAGMENT}
  mutation UpdateAppointment($input: UpdateAppointmentInput!) {
    updateSchedule(input: $input) {
      schedule {
        ...AppointmentFragment
      }
    }
  }
`;

const GET_APPOINTMENT = gql`
  ${APPOINTMENT_FRAGMENT}
  query GetAppointment($id: Int!) {
    schedule(id: $id) {
      ...AppointmentFragment
    }
  }
`;

const createAppointmentSchema = zod.object({
  duration: zod.number(),
  isAllDay: zod.boolean(),
  priority: zod.enum(['HIGH', 'LOW', 'NORMAL']).nullable(),
  startAt: zod.date(),
  endAt: zod.date(),
  status: zod.enum(['SCHEDULED', 'RESCHEDULED', 'CANCELLED', 'NO_SHOW', 'COMPLETED']),
  attendees: zod.array(
    zod.union([
      z.object({
        id: z.number(),
        _type: z.literal('USER'),
      }),
      z.object({
        email: z.string(),
        _type: z.literal('EMAIL'),
      }),
      z.object({
        id: z.number(),
        _type: z.literal('CONTACT'),
      }),
    ]),
  ),
  title: zod.string(),
  description: zod.string().max(100).nullable(),
  assignedTo: zod
    .number()
    .nullable()
    .refine((value): boolean => value !== null),
  contactId: zod.number().nullable(),
});

type CreateAppointmentFormValues = zod.infer<typeof createAppointmentSchema>;

const BottomContentContainer = styled(FlexRow)`
  gap: 8px;
  height: 35px;
  flex-wrap: wrap;
  min-height: 35px;
  height: max-content;
`;

const TitleInputContainer = styled(FlexColumn)`
  margin: 8px 10px;
  width: 100%;
`;

const optionIconBaseStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  width: 16px;
`;

const AppointmentPriorityIcon = styled(Icon)<{ $priority: SchedulePriority }>`
  ${optionIconBaseStyles}
  background-color: ${({ $priority, theme }) => {
    if ($priority === 'HIGH') {
      return theme.colors.secondaryCoral80;
    }

    if ($priority === 'LOW') {
      return theme.colors.secondaryTopaz80;
    }

    return theme.colors.secondaryGold80;
  }};
  border-radius: 50%;

  svg {
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }
`;

const ClearPriorityIcon = styled(Icon)`
  ${optionIconBaseStyles};

  svg {
    width: 12px;
    height: 12px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const InfoIcon = styled(Icon)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.koala};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 12px;
  justify-content: center;
  margin-left: 3px;
  width: 12px;

  svg {
    height: 6.96px;

    path {
      fill: ${({ theme }) => theme.colors.storm};
    }
  }
`;

const DateAndTimeInput = styled(StyledInlineTextInput)`
  ${down('md')} {
    border: none;
    field-sizing: content;
  }
`;

const getAppointmentDefaultValues = (
  appointment: Schedule,
): CreateAppointmentFormValues => {
  const attendees = appointment.attendees
    .map((attendee) => {
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      return match(attendee)
        .with({ __typename: 'ScheduleUserAttendee' }, (attendee) => ({
          _type: 'USER' as const,
          id: attendee.user.id,
        }))
        .with({ __typename: 'ScheduleContactAttendee' }, (attendee) => ({
          _type: 'CONTACT' as const,
          id: attendee.contact.id,
        }))
        .with({ __typename: 'ScheduleEmailAttendee' }, (attendee) => ({
          _type: 'EMAIL' as const,
          email: attendee.email,
        }))
        .exhaustive();
    })
    .filter(Boolean);

  return {
    title: appointment.title,
    description: appointment.description ?? '',
    duration: 0,
    isAllDay: appointment.isAllDay,
    priority: appointment.priority as SchedulePriority,
    status: appointment.status,
    attendees: attendees as CreateAppointmentFormValues['attendees'],
    contactId: appointment.contact?.id ?? null,
    startAt: appointment.startAt,
    endAt: appointment.endAt,
    assignedTo: appointment.user?.id ?? null,
  };
};

const AttendeeSelectWrapper = styled(FlexRow)`
  .lgg-select__control {
    .lgg-select__value-container {
      padding-top: 1px;
      padding-bottom: 1px;
    }
  }

  ${down('md')} {
    .select-wrapper {
      width: min-content;

      .lgg-select__value-container--is-multi {
        &.lgg-select__value-container--has-value {
          justify-content: flex-end;

          .lgg-select__multi-value {
            .lgg-select__multi-value__remove {
              display: none;
            }
          }
        }
      }
    }
  }
`;

// Free-text inputs are saved when the user leaves them rather than while typing.
const AUTOSAVE_ON_BLUR_FIELDS: Path<CreateAppointmentFormValues>[] = [
  'title',
  'description',
];

export const AppointmentInlineFormModal = memo(() => {
  const currentInstitution = useCurrentInstitution();
  const { id: currentUserId } = useCurrentUser();
  const modalVisibilityHandler = useVisible(true);
  const { goBack } = useModalUrl({
    backgroundLocation: `/i/company/${currentInstitution.id}/cms/appointments/`,
  });
  const breakpointUpMd = useBreakpoint(up('md'));
  const { t } = useTranslation(['common', 'appointments']);
  const handlecloseModal = () => {
    createAppointmentForm.reset();
    goBack();
    modalVisibilityHandler.close();
  };
  const { scheduleId } = useParams<{
    scheduleId?: string;
  }>();
  const isCreatingAppointment = !scheduleId;
  const showNotification = useShowNotification();
  const showConfirmationModal = useShowConfirmationModal();
  const { initDayJsDate, addMinutes } = useDateHelpers();
  const { formatDateAndTimePickerDate } = useFormatDate();

  const handleGetAppointmentError = useCallback(() => {
    modalVisibilityHandler.close();
    goBack();
    showNotification({
      title: t('appointments:appointmentNotFoundError.title'),
      message: t('appointments:appointmentNotFoundError.message'),
      type: 'error',
    });
  }, [modalVisibilityHandler, goBack, showNotification, t]);

  const { hasPermission } = useAuthorization();
  const hasAppointmentManagePermission = hasPermission(
    'crm:company.appointment:update:all',
  );
  const [isAppointmentOwner, setIsAppointmentOwner] = useState(false);

  const handleGraphQLError = useHandleGraphQLError();
  const showCloseConfirmationModal = useShowCloseConfirmationModal();
  const { getAppointmentUrl } = useUrls();
  const searchParams = new URLSearchParams(window.location.search);
  const targetDate = searchParams.get('date');
  const targetContact = searchParams.get('contactId');
  const parsedTargetDate = dayjs(targetDate ?? undefined);
  const roundedTargetDate = roundToMinutesInterval(parsedTargetDate);
  const hasLoadedAppointmentRef = useRef(false);

  // On creation, the user creating the appointment is added as an attendee by default
  // (it can be removed). When created from a contact's context, that contact is also a
  // fixed attendee (it can't be removed).
  const fixedContactAttendees = useMemo<AttendeeCandidate[]>(
    () =>
      isCreatingAppointment && targetContact
        ? [{ type: 'CONTACT', id: Number(targetContact) }]
        : [],
    [isCreatingAppointment, targetContact],
  );

  const [defaultValues, setDefaultValues] = useState<CreateAppointmentFormValues>({
    contactId: null,
    assignedTo: null,
    title: '',
    description: '',
    duration: 0,
    isAllDay: false,
    priority: null,
    status: 'SCHEDULED',
    attendees: [],
    startAt: roundedTargetDate.toDate(),
    endAt: addMinutes(roundedTargetDate.toDate(), 45),
  });

  const createAppointmentForm = useForm<CreateAppointmentFormValues>({
    resolver: zodResolver(
      createAppointmentSchema.extend({
        title: zod.string().min(1, {
          message: t('appointments:appointmentInlineForm.fields.title.errors.required'),
        }),
        description: zod.string().max(100, {
          message: t(
            'appointments:appointmentInlineForm.fields.description.errors.maxLength',
          ),
        }),
        attendees: createAppointmentSchema.shape.attendees.min(1, {
          message: t(
            'appointments:appointmentInlineForm.fields.attendees.errors.required',
          ),
        }),
      }),
    ),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (isCreatingAppointment) {
      createAppointmentForm.setValue('contactId', Number(targetContact));
      createAppointmentForm.setValue('assignedTo', currentUserId);

      createAppointmentForm.setValue('attendees', [
        ...(targetContact
          ? [{ _type: 'CONTACT' as const, id: Number(targetContact) }]
          : []),
        { _type: 'USER', id: currentUserId },
      ]);
    }
  }, [isCreatingAppointment, targetContact, currentUserId, createAppointmentForm]);

  const watchIsAllDay = createAppointmentForm.watch('isAllDay');
  const watchStartDate = createAppointmentForm.watch('startAt');
  const watchEndDate = createAppointmentForm.watch('endAt');
  const watchPriority = createAppointmentForm.watch('priority');

  const { loading: getAppointmentLoading } = useQuery<
    Pick<Query, 'schedule'>,
    QueryScheduleArgs
  >(GET_APPOINTMENT, {
    variables: { id: Number(scheduleId) },
    skip: !scheduleId,
    onCompleted: ({ schedule }) => {
      if (hasLoadedAppointmentRef.current) return;

      hasLoadedAppointmentRef.current = true;

      if (!schedule) {
        handleGetAppointmentError();
        return;
      }

      if (schedule.user?.id === currentUserId) {
        setIsAppointmentOwner(true);
      }

      const values = getAppointmentDefaultValues(schedule);

      const newValues = {
        ...values,
        startAt: dayjs(schedule.startAt).toDate(),
        endAt: dayjs(schedule.endAt).toDate(),
      };

      setDefaultValues(newValues);
      createAppointmentForm.reset(newValues);
    },
    onError: handleGetAppointmentError,
  });

  const [createAppointment, { loading: createAppointmentLoading }] = useMutation(
    CREATE_APPOINTMENT_MUTATION,
  );
  const [updateAppointment, { loading: updateAppointmentLoading }] = useMutation(
    UPDATE_APPOINTMENT_MUTATION,
  );
  const [deleteAppointment, { loading: deleteAppointmentLoading }] =
    useDeleteAppointment();

  const {
    formState: { isDirty, dirtyFields },
  } = createAppointmentForm;

  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;
  const appointmentStatusOptions = useAppointmentsStatusOptions({ includeIcons: true });
  const appointmentsPriorityOptions = useAppointmentsPriorityOptions({
    includeIcons: true,
  });

  const getRequestVariables = useCallback(
    (values: Partial<CreateAppointmentFormValues>) => {
      const toScheduleDateInput = (value: string) =>
        values.isAllDay ? value.slice(0, 10) : value;

      const sharedVariables = {
        title: values.title,
        description: values.description ?? '',
        duration: 15,
        isAllDay: values.isAllDay,
        priority: values.priority as SchedulePriority,
        status: values.status?.replaceAll('-', '_') as ScheduleStatus,
        emailAttendees: values?.attendees
          ?.filter((attendee) => attendee._type === 'EMAIL')
          .map((attendee) => attendee.email),
        contactAttendeeIds: values?.attendees
          ?.filter((attendee) => attendee._type === 'CONTACT')
          .map((attendee) => attendee.id),
        userAttendeeIds: values?.attendees
          ?.filter((attendee) => attendee._type === 'USER')
          .map((attendee) => attendee.id),
        assignedTo: values.assignedTo,
        startAt: toScheduleDateInput(values.startAt?.toISOString() ?? ''),
        endAt: toScheduleDateInput(values.endAt?.toISOString() ?? ''),
        contactId: values.contactId ? Number(values.contactId) : null,
      };

      return sharedVariables;
    },
    [],
  );

  const updateAppointmentRecord = useCallback(
    async (
      values: Partial<CreateAppointmentFormValues>,
      { onCompleted }: { onCompleted: () => void },
    ) => {
      await updateAppointment({
        variables: {
          input: {
            ...getRequestVariables(values),
            id: Number(scheduleId),
          },
        },
        onCompleted,
        onError: handleGraphQLError,
      });
    },
    [getRequestVariables, updateAppointment, scheduleId, handleGraphQLError],
  );

  const onAutosaveAppointment = useCallback(
    async (values: Partial<CreateAppointmentFormValues>) => {
      await updateAppointmentRecord(values, {
        onCompleted: () => {
          (Object.keys(values) as (keyof CreateAppointmentFormValues)[]).forEach(
            (name) => {
              // If the field changed again while the mutation was in flight, don't
              // mark it clean: leave it dirty so the next autosave cycle retries it.
              if (isEqual(createAppointmentForm.getValues(name), values[name])) {
                createAppointmentForm.resetField(name, { defaultValue: values[name] });
              }
            },
          );
        },
      });
    },
    [updateAppointmentRecord, createAppointmentForm],
  );

  const appointmentsPriorityOptionsWithIcons = useMemo(() => {
    const priorityOptions = appointmentsPriorityOptions.map((option) => ({
      ...option,
      icon: (
        <AppointmentPriorityIcon
          type={option.icon as string}
          $priority={option.value as SchedulePriority}
        />
      ),
    }));

    return [
      ...priorityOptions,
      ...(watchPriority
        ? [
            {
              label: t(
                'appointments:appointmentInlineForm.fields.priority.options.clear',
              ),
              value: 'CLEAR-PRIORITY',
              icon: <ClearPriorityIcon type="close" />,
            },
          ]
        : []),
    ];
  }, [appointmentsPriorityOptions, t, watchPriority]);

  const canManageAppointment = useMemo(() => {
    return isAppointmentOwner || hasAppointmentManagePermission;
  }, [isAppointmentOwner, hasAppointmentManagePermission]);

  const statusInput = useMemo(() => {
    return (
      <Controller
        control={createAppointmentForm.control}
        name="status"
        render={({ field, fieldState }) => {
          const error = fieldState.error?.message;
          const props = {
            name: 'upsert-appointment-field-status',
            mobileLabel: t('appointments:appointmentInlineForm.fields.status.label'),
            value: appointmentStatusOptions.find(
              (option) => option.value === field.value,
            ),
            hasError: Boolean(error),
            isClearable: false,
            isMulti: false,
            isSearchable: false,
            options: appointmentStatusOptions,
            hideIndicators: true,
            onChange: (value) => {
              field.onChange((value as SelectOption<string>)?.value);
            },
          };

          return breakpointUpMd ? (
            <DesktopSelect {...field} {...props} />
          ) : (
            <InlineInputWrapper
              label={t('appointments:appointmentInlineForm.fields.status.label')}
              error={error}
              icon="status"
            >
              <InlineFormSelect {...props} />
            </InlineInputWrapper>
          );
        }}
      />
    );
  }, [appointmentStatusOptions, breakpointUpMd, createAppointmentForm.control, t]);

  const priorityInput = useMemo(() => {
    return (
      <Controller
        control={createAppointmentForm.control}
        name="priority"
        render={({ field, fieldState }) => {
          const error = fieldState.error?.message;
          const props = {
            name: 'upsert-appointment-field-priority',
            mobileLabel: t('appointments:appointmentInlineForm.fields.priority.label'),
            value: appointmentsPriorityOptionsWithIcons.find(
              (option) => option.value === field.value,
            ),
            placeholder: t(
              'appointments:appointmentInlineForm.fields.priority.placeholder',
            ),
            hasError: Boolean(error),
            isClearable: false,
            isMulti: false,
            isSearchable: false,
            options: appointmentsPriorityOptionsWithIcons,
            hideIndicators: true,
            onChange: (value) => {
              if (value?.value === 'CLEAR-PRIORITY') {
                createAppointmentForm.setValue('priority', null);
                return;
              }

              field.onChange(!value || value.value === field.value ? null : value.value);
            },
          };

          return breakpointUpMd ? (
            <DesktopSelect {...field} {...props} />
          ) : (
            <InlineInputWrapper
              label={t('appointments:appointmentInlineForm.fields.priority.label')}
              error={error}
              icon="priority"
            >
              <InlineFormSelect {...props} />
            </InlineInputWrapper>
          );
        }}
      />
    );
  }, [appointmentsPriorityOptionsWithIcons, breakpointUpMd, createAppointmentForm, t]);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <InlineFormModal<CreateAppointmentFormValues>
      title={
        isCreatingAppointment
          ? t('appointments:appointmentInlineForm.title.create')
          : t('appointments:appointmentInlineForm.title.update')
      }
      submitButtonText={t('appointments:appointmentInlineForm.submitButtonText.create')}
      testId="create-appointment-modal"
      isLoading={getAppointmentLoading}
      disableSubmitButton={!isCreatingAppointment && !canManageAppointment}
      isSubmitting={createAppointmentLoading || updateAppointmentLoading}
      visibilityHandler={modalVisibilityHandler}
      form={createAppointmentForm}
      onCancel={() => {
        if (!isFormDirty) {
          handlecloseModal();
          return;
        }

        showCloseConfirmationModal({
          onCancel: handlecloseModal,
        });
      }}
      onSubmit={async (values) => {
        await createAppointment({
          variables: {
            input: {
              institutionId: currentInstitution.id,
              ...getRequestVariables(values),
              contactId: values.contactId ? Number(values.contactId) : null,
            },
          },
          onCompleted: () => {
            showNotification({
              title: t('appointments:appointmentCreated'),
              type: 'success',
            });
            handlecloseModal();
          },
          onError: handleGraphQLError,
        });
      }}
      autoSaveEnabled={!isCreatingAppointment && canManageAppointment}
      autosaveOnBlurFields={AUTOSAVE_ON_BLUR_FIELDS}
      onAutosave={async () => {
        await onAutosaveAppointment(createAppointmentForm.getValues());
      }}
      topContent={
        <Controller
          control={createAppointmentForm.control}
          name="title"
          render={({ field }) => (
            <TitleInputContainer>
              <BigTextInput
                {...field}
                data-lgg-id="upsert-appointment-field-title"
                showErrorMessage
                error={createAppointmentForm.formState.errors.title?.message}
                placeholder={t(
                  'appointments:appointmentInlineForm.fields.title.placeholder',
                )}
                label={t('appointments:appointmentInlineForm.fields.title.label')}
              />
            </TitleInputContainer>
          )}
        />
      }
      bottomContent={
        breakpointUpMd ? (
          <BottomContentContainer>
            {statusInput}
            {priorityInput}
          </BottomContentContainer>
        ) : null
      }
      moreOptions={
        isCreatingAppointment
          ? []
          : [
              {
                icon: 'link',
                label: t(
                  'appointments:appointmentInlineForm.actions.copyAppointmentLink.label',
                ),
                'data-lgg-id': 'appointment-more-options-copy-link',
                onClick: () => {
                  const url = getAppointmentUrl({
                    appointmentId: Number(scheduleId),
                    options: { includeHost: true },
                  });
                  copyUrlToClipboard(url);
                  showNotification({
                    title: t(
                      'appointments:appointmentInlineForm.actions.copyAppointmentLink.successMessage',
                    ),
                    type: 'success',
                  });
                },
              },
              ...(canManageAppointment
                ? [
                    {
                      label: t(
                        'appointments:appointmentInlineForm.actions.deleteAppointment.label',
                      ),
                      render: () => {
                        return (
                          <InlineFormDeleteOption
                            label={t(
                              'appointments:appointmentInlineForm.actions.deleteAppointment.label',
                            )}
                            icon="deleteBin"
                            data-lgg-id="appointment-more-options-delete"
                            disabled={deleteAppointmentLoading}
                            onClick={async () => {
                              showConfirmationModal({
                                title: t(
                                  'appointments:appointmentInlineForm.actions.deleteAppointment.confirmation.title',
                                ),
                                message: t(
                                  'appointments:appointmentInlineForm.actions.deleteAppointment.confirmation.message',
                                ),
                                confirmButtonText: t(
                                  'appointments:appointmentInlineForm.actions.deleteAppointment.confirmation.confirmButtonText',
                                ),
                                confirmButtonType: 'delete',
                                testId: 'delete-appointment-confirmation-modal',
                                onConfirm: async () => {
                                  await deleteAppointment({
                                    variables: {
                                      input: {
                                        scheduleId: Number(scheduleId),
                                      },
                                    },
                                    onCompleted: () => {
                                      handlecloseModal();
                                    },
                                    onError: (error) => {
                                      handleGraphQLError(error);
                                    },
                                    update(cache) {
                                      const normalizedId = cache.identify({
                                        id: scheduleId,
                                        __typename: 'Schedule',
                                      });
                                      cache.evict({ id: normalizedId });
                                      cache.gc();
                                    },
                                  });
                                },
                              });
                            }}
                          />
                        );
                      },
                    },
                  ]
                : []),
            ]
      }
    >
      <FlexColumn>
        {breakpointUpMd ? null : statusInput}
        <Controller
          control={createAppointmentForm.control}
          name="description"
          render={({ field }) => {
            const error = createAppointmentForm.formState.errors.description?.message;

            return (
              <InlineTextArea
                {...field}
                label={t('appointments:appointmentInlineForm.fields.description.label')}
                icon="draft"
                error={error}
                ref={textAreaRef}
                placeholder={t(
                  'appointments:appointmentInlineForm.fields.description.placeholder',
                )}
                data-lgg-id="upsert-appointment-field-description"
                onChange={(e) => {
                  const value = e.target.value;

                  field.onChange(value);
                }}
                onBlur={(e) => {
                  e.currentTarget.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            );
          }}
        />
        <Controller
          control={createAppointmentForm.control}
          name="assignedTo"
          render={({ field, formState }) => {
            const error = formState.errors.assignedTo?.message;

            return (
              <InlineInputWrapper
                label={
                  <FlexRow style={{ alignItems: 'center' }}>
                    <span>
                      {t('appointments:appointmentInlineForm.fields.assignee.label')}
                    </span>
                    {breakpointUpMd ? (
                      <Tooltip
                        title={t(
                          'appointments:appointmentInlineForm.fields.assignee.tooltip',
                        )}
                        overlayStyle={{ width: 188 }}
                        overlayInnerStyle={{ textAlign: 'center' }}
                      >
                        <InfoIcon type="info" />
                      </Tooltip>
                    ) : null}
                  </FlexRow>
                }
                error={error}
                icon="assignedto"
                showErrorMessage={false}
              >
                <InlineFormUserSelect
                  mobileLabel={t(
                    'appointments:appointmentInlineForm.fields.assignee.label',
                  )}
                  selectedUsersIds={field.value ? [field.value] : []}
                  hasError={Boolean(error)}
                  isMulti={false}
                  isClearable={!breakpointUpMd}
                  onChange={(value) => {
                    field.onChange(
                      !value || value.value === field.value ? null : value.value,
                    );
                  }}
                  name="upsert-appointment-field-assigned-to"
                  hideIndicators
                  renderValue={(value) => <UserSelectValue value={value} />}
                  renderPlaceholder={() => (
                    <UserSelectPlaceholder
                      placeholder={t(
                        'appointments:appointmentInlineForm.fields.assignee.placeholder',
                      )}
                    />
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={createAppointmentForm.control}
          name="contactId"
          render={({ field, fieldState }) => {
            const error = fieldState.error?.message;

            return (
              <InlineInputWrapper
                label={t(
                  'appointments:appointmentInlineForm.fields.relatedContact.label',
                )}
                error={error}
                icon="contactAlt"
              >
                <InlineFormContactSelect
                  selectedContactIds={field.value ? [field.value] : []}
                  isMulti={false}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  isDisabled={!isCreatingAppointment}
                  isClearable={false}
                  hideIndicators
                  name="upsert-appointment-field-related-contact"
                  mobileLabel={t(
                    'appointments:appointmentInlineForm.fields.relatedContact.label',
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={createAppointmentForm.control}
          name="attendees"
          render={({ field, fieldState }) => {
            const error = fieldState.error?.message;

            return (
              <InlineInputWrapper
                label={t('appointments:appointmentInlineForm.fields.attendees.label')}
                error={error}
                icon="attendees"
                showErrorMessage={false}
              >
                <AttendeeSelectWrapper>
                  <AttendeesSelect
                    mobileLabel={t(
                      'appointments:appointmentInlineForm.fields.attendees.label',
                    )}
                    selectedAttendees={
                      field.value?.map((attendee) =>
                        attendee._type === 'EMAIL'
                          ? { type: 'EMAIL' as const, email: attendee.email }
                          : { type: attendee._type, id: attendee.id },
                      ) ?? []
                    }
                    hasError={Boolean(error)}
                    isClearable={!breakpointUpMd}
                    onChange={(values) => {
                      field.onChange(
                        values.map((value) =>
                          value.type === 'EMAIL'
                            ? { _type: 'EMAIL' as const, email: value.email }
                            : { id: value.id, _type: value.type },
                        ),
                      );
                    }}
                    name="upsert-appointment-field-attendees"
                    fixedAttendees={fixedContactAttendees}
                    hideIndicators
                    multiValueMaxToShow={breakpointUpMd ? 3 : 2}
                  />
                </AttendeeSelectWrapper>
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={createAppointmentForm.control}
          name="startAt"
          render={({ field, fieldState }) => {
            const error = fieldState.error?.message;
            const hasValue = watchEndDate && watchStartDate;
            const value = hasValue
              ? `${formatDateAndTimePickerDate(
                  initDayJsDate(new Date(watchStartDate)).toDate(),
                  !watchIsAllDay,
                )} - ${formatDateAndTimePickerDate(
                  initDayJsDate(new Date(watchEndDate)).toDate(),
                  !watchIsAllDay,
                )}`
              : undefined;

            const input = (
              <DateAndTimeInput
                {...field}
                data-lgg-id="upsert-appointment-field-date-and-time"
                readOnly
                placeholder={t(
                  'appointments:appointmentInlineForm.fields.dateAndTime.placeholder',
                )}
                value={value}
              />
            );

            return (
              <InlineInputWrapper
                label={t('appointments:appointmentInlineForm.fields.dateAndTime.label')}
                error={error}
                icon="calendarNoPadding"
                showErrorMessage={false}
              >
                <AppointmentDateTimePicker
                  onApply={({ startDate, endDate, isAllDay }) => {
                    createAppointmentForm.setValue('startAt', startDate);

                    if (endDate) {
                      createAppointmentForm.setValue('endAt', endDate);
                    }

                    createAppointmentForm.setValue('isAllDay', isAllDay);
                  }}
                  startDate={field.value ? field.value : undefined}
                  endDate={watchEndDate ? watchEndDate : undefined}
                  isAllDay={watchIsAllDay}
                >
                  {input}
                </AppointmentDateTimePicker>
              </InlineInputWrapper>
            );
          }}
        />
        {breakpointUpMd ? null : priorityInput}
      </FlexColumn>
    </InlineFormModal>
  );
});
